import {
  Component,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TeaserComponent } from '../../molecules/teaser/teaser.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import TeaserType from '../../../shared/enums/teaser.type.enums';
import { Colors, Headlines, Sizes } from '../../../shared/enums/shared.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { News } from '../../../shared/interfaces/news.interface';
import { TeaserShowcaseType } from '../../../shared/enums/teaser-showcase-type.enums';
import { Teaser } from '../../../shared/interfaces/teaser.interface';
import { ImageMetaData } from '../../../shared/interfaces/meta-data.interface';
import { ImagesService } from '../../../services/images.service';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { HrefType } from '../../../shared/enums/href-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';
import { TeaserContainerComponent } from '../teaser-container/teaser-container.component';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-dynamic-teaser-showcase',
  standalone: true,
  imports: [
    CommonModule,
    TeaserComponent,
    TeaserContainerComponent,
    NgxSkeletonLoaderModule,
  ],
  templateUrl: './dynamic-teaser-showcase.component.html',
  styleUrls: ['./dynamic-teaser-showcase.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class DynamicTeaserShowcaseComponent implements OnChanges {
  @Input() news: News[];
  @Input() teasers: Teaser[];
  @Input() type: TeaserShowcaseType = TeaserShowcaseType.NEWS;
  @Input() imagesMetaData?: ImageMetaData[];
  @ViewChild('teasersOverview') teasersOverview: TeaserContainerComponent =
    {} as TeaserContainerComponent;
  public showNewsContainer: boolean = true;
  public showOldNewsContainer: boolean = true;
  public isLoading = true;

  public readonly TeaserShowcaseType = TeaserShowcaseType;
  public readonly HrefType = HrefType;
  public readonly LinkType = LinkType;
  constructor(
    private readonly imagesService: ImagesService,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    this.news = [];
    this.teasers = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imagesMetaData']) {
      this.imagesService.setImagesMetaData(
        changes['imagesMetaData'].currentValue
      );
    }

    if (changes['news']) {
      this.showOldNewsContainer = false;
      setTimeout(() => {
        this.showOldNewsContainer = true;
      }, 100);

      this.news.forEach((news, index) => {
        // Wrap imageAlt assignment
        safeAssign(
          () => {
            news.imageAlt =
              this.imagesService.getAltTextById(news.imageIdentifier) ?? '';
          },
          `Set imageAlt for news at index ${index}`,
          this.errorHandler
        );

        // Wrap link construction
        safeAssign(
          () => {
            if (news.rawLink) {
              const rawLink = news.rawLink;
              let href: string = '';
              let hrefType: HrefType = HrefType.ANGULAR_ROUTE;
              let title: string = '';
              let label: string = '';
              let linkType: LinkType = LinkType.PRIMARY_ARROW;
              safeAssign(
                () => {
                  href = this.caasBodytextService.getHref(rawLink);
                },
                'Get href',
                this.errorHandler
              );
              safeAssign(
                () => {
                  hrefType = this.caasBodytextService.getHrefType(rawLink);
                },
                'Get hrefType',
                this.errorHandler
              );
              safeAssign(
                () => {
                  title = rawLink.value?.formData?.lt_link_title?.value ?? '';
                },
                'Get title',
                this.errorHandler
              );
              safeAssign(
                () => {
                  label = rawLink.value?.formData?.lt_link_label?.value ?? '';
                },
                'Get label',
                this.errorHandler
              );
              safeAssign(
                () => {
                  linkType =
                    this.caasBodytextService.checkOptionalLinksType(rawLink);
                },
                'Check link type',
                this.errorHandler
              );

              news.link = {
                href: href,
                hrefType: hrefType,
                title: title,
                label: label,
                linkType: linkType,
              };
            }
          },
          `Set link object for news at index ${index}`,
          this.errorHandler
        );
      });
    }
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  TeaserType = TeaserType;
  Colors = Colors;
  BadgeType = BadgeType;
  Headlines = Headlines;
  Sizes = Sizes;
}
